<template><div><code-example only-show="demo">
<dt-stack direction="row" gap="100">
  <dt-filter-pill
    :model-value="[{name: 'Address', active: true}, {name: 'Call Purpose', active: true}, {name: 'Action Item'}, {name: 'Negative Sentiment'}, {name: 'Warranty Inquiry', active: true}]"
    label="Moment"
    end-tooltip-text="Remove"
  >
    <template #default="{ label, filters, activeFilters }">
      {{ label }}<template v-if="activeFilters.length">:
      <strong>
        {{ activeFilters.length === filters.length ? 'All' : activeFilters.length }}
      </strong></template>
    </template>
  </dt-filter-pill>
  <dt-filter-pill
    v-model="heroConversationTypes"
    :start-tooltip-text="selectedHeroConversationType !== 'All Conversations'
      ? 'Conversation type'
      : ''"
    end-tooltip-text="Remove"
    use-dropdown
    @clear="resetHeroConversationType"
  >
    <template #default>
      {{ selectedHeroConversationType === 'All Conversations'
        ? 'Conversation type'
        : selectedHeroConversationType }}
    </template>
    <template #content="{ close }">
      <dt-list-item
        v-for="filter in heroConversationTypes"
        :key="filter.name"
        role="menuitem"
        navigation-type="arrow-keys"
        :selected="filter.name === selectedHeroConversationType"
        @click="selectHeroConversationType(filter.name, close)"
      >
        {{ filter.name }}
      </dt-list-item>
    </template>
  </dt-filter-pill>
  <dt-filter-pill
    :model-value="[{name: 'Email'}, {name: 'Phone', active: true}, {name: 'Chat'}, {name: 'Social'}, {name: 'SMS'}]"
    label="Channel"
    end-tooltip-text="Remove"
    popover-footer-class="d-pie-200 d-py-150"
    defer-selection
  >
  </dt-filter-pill>
  <dt-button :size="200" kind="muted" importance="outlined" :disabled="!heroHasActiveFilters" @click="resetHeroFilters">
    Reset
  </dt-button>
</dt-stack>
</code-example>
<component-combinator component-name="DtFilterPill" /><h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<h3 id="key-concepts" tabindex="-1" class="d-docsite--header-3">Key concepts <a class="header-anchor d-link d-docsite--link d-link" href="#key-concepts" aria-hidden="true">#</a></h3>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><code v-pre>v-model</code> expects an array of <code v-pre>{ name, active? }</code> objects. The pill is &quot;active&quot; when any item has <code v-pre>active: true</code>.</li>
<li class="d-docsite--list-element">The <code v-pre>default</code> scoped slot overrides the pill label and receives: <code v-pre>{ label, filters, activeFilters, activeFilterList, activeFilterOverflow }</code>.</li>
<li class="d-docsite--list-element">The <code v-pre>content</code> slot overrides the popover body, replacing the default checkbox list with custom content (e.g., radio groups).</li>
<li class="d-docsite--list-element"><strong>In-place mutation</strong>: The pill copies the <code v-pre>modelValue</code> array at mount. To keep active state in sync, mutate objects with <code v-pre>forEach</code> rather than replacing the array with <code v-pre>map</code>.</li>
</ul>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Use to filter a list, table, or other data set by a specific attribute (e.g., channel, contact center, date range).</li>
<li class="d-docsite--list-element">Use the default checkbox popover for multi-select filters where several options can be active at once.</li>
<li class="d-docsite--list-element">Use <code v-pre>use-dropdown</code> for single-select filters where only one option applies at a time (e.g., conversation type).</li>
<li class="d-docsite--list-element">Provide <code v-pre>end-tooltip-text</code> on the clear button so it has an accessible name for screen readers.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Don't use as a general-purpose action button — filter pills are for narrowing data, not triggering commands.</li>
<li class="d-docsite--list-element">Don't use for binary on/off settings — use a <RouteLink to="/components/toggle.html" class="d-docsite--link d-link">Toggle</RouteLink>.</li>
<li class="d-docsite--list-element">Don't omit <code v-pre>label</code> and the <code v-pre>default</code> slot — the pill must always have visible text identifying the filter category.</li>
<li class="d-docsite--list-element">Don't replace the array with <code v-pre>map</code> when toggling active state — mutate objects in-place with <code v-pre>forEach</code> to keep the pill's internal copy in sync.</li>
</ul>
</template>
</dialtone-usage>
<h2 id="variants" tabindex="-1" class="d-docsite--header-2">Variants <a class="header-anchor d-link d-docsite--link d-link" href="#variants" aria-hidden="true">#</a></h2>
<h3 id="base" tabindex="-1" class="d-docsite--header-3">Base <a class="header-anchor d-link d-docsite--link d-link" href="#base" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-filter-pill
  :model-value=&quot;[{name: &#39;Email&#39;}, {name: &#39;Phone&#39;}, {name: &#39;Chat&#39;}, {name: &#39;Social&#39;}, {name: &#39;SMS&#39;}]&quot;
  label=&quot;Channel&quot;
&gt;
&lt;/dt-filter-pill&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <dt-filter-pill
    :model-value="[{name: 'Email'}, {name: 'Phone'}, {name: 'Chat'}, {name: 'Social'}, {name: 'SMS'}]"
    label="Channel"
  >
  </dt-filter-pill>
</dt-stack>
</code-example>
<h3 id="active" tabindex="-1" class="d-docsite--header-3">Active <a class="header-anchor d-link d-docsite--link d-link" href="#active" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The pill becomes active when any filter item has <code v-pre>active: true</code>.</p>
<code-example source-code='&lt;dt-filter-pill
  :model-value=&quot;[{name: &#39;Headquarters&#39;, active: true}, {name: &#39;Westside&#39;}, {name: &#39;Downtown&#39;}]&quot;
  label=&quot;Contact centers&quot;
&gt;
&lt;/dt-filter-pill&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <dt-filter-pill
    :model-value="[{name: 'Headquarters', active: true}, {name: 'Westside'}, {name: 'Downtown'}]"
    label="Contact centers"
  >
  </dt-filter-pill>
</dt-stack>
</code-example>
<h3 id="disabled" tabindex="-1" class="d-docsite--header-3">Disabled <a class="header-anchor d-link d-docsite--link d-link" href="#disabled" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-filter-pill label=&quot;Conversation type&quot; disabled&gt;&lt;/dt-filter-pill&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <dt-filter-pill label="Conversation type" disabled></dt-filter-pill>
</dt-stack>
</code-example>
<h3 id="read-only" tabindex="-1" class="d-docsite--header-3">Read only <a class="header-anchor d-link d-docsite--link d-link" href="#read-only" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Its value is reflected in the filter set but cannot be opened, cleared, or modified. Functionally and visually distinct from disabled.</p>
<code-example source-code='&lt;dt-filter-pill
  :model-value=&quot;[{name: &#39;Headquarters&#39;, active: true}, {name: &#39;Westside&#39;, active: true}, {name: &#39;Downtown&#39;}]&quot;
  label=&quot;Contact centers&quot;
  read-only
&gt;&lt;/dt-filter-pill&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <dt-filter-pill
    :model-value="[{name: 'Headquarters', active: true}, {name: 'Westside', active: true}, {name: 'Downtown'}]"
    label="Contact centers"
    read-only
  ></dt-filter-pill>
</dt-stack>
</code-example>
<h3 id="size" tabindex="-1" class="d-docsite--header-3">Size <a class="header-anchor d-link d-docsite--link d-link" href="#size" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph"><code v-pre>200</code> (small) is the default.</p>
<code-example source-code='&lt;dt-filter-pill label=&quot;{size}&quot; size=&quot;{size}&quot; /&gt;'>
<dt-stack direction="row" gap="100">
  <dt-filter-pill
    v-for="size in sizes"
    :key="size"
    :label="size"
    :size="size"
  ></dt-filter-pill>
</dt-stack>
</code-example>
<h2 id="interaction-patterns" tabindex="-1" class="d-docsite--header-2">Interaction patterns <a class="header-anchor d-link d-docsite--link d-link" href="#interaction-patterns" aria-hidden="true">#</a></h2>
<h3 id="clearable" tabindex="-1" class="d-docsite--header-3">Clearable <a class="header-anchor d-link d-docsite--link d-link" href="#clearable" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">A clear button appears when any filter is active. It emits the <code v-pre>reset</code> event when clicked.</p>
<code-example source-code='&lt;dt-filter-pill
  label=&quot;Channel&quot;
  :model-value=&quot;[{ name: &#39;Option 1&#39; }, { name: &#39;Option 2&#39;, active: true }, { name: &#39;Option 3&#39; }]&quot;
  end-tooltip-text=&quot;Remove&quot;
&gt;
&lt;/dt-filter-pill&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <dt-filter-pill
    label="Channel"
    :model-value="[{ name: 'Option 1' }, { name: 'Option 2', active: true }, { name: 'Option 3' }]"
    end-tooltip-text="Remove"
  >
  </dt-filter-pill>
</dt-stack>
</code-example>
<h3 id="non-clearable" tabindex="-1" class="d-docsite--header-3">Non clearable <a class="header-anchor d-link d-docsite--link d-link" href="#non-clearable" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Setting the <code v-pre>:show-clear=&quot;false&quot;</code> prop hides the reset/clear button.</p>
<code-example source-code='&lt;dt-filter-pill
  :model-value=&quot;[{name: &#39;0–5 min&#39;, active: true}, {name: &#39;5–15 min&#39;}, {name: &#39;15–30 min&#39;}, {name: &#39;30+ min&#39;}]&quot;
  label=&quot;Duration&quot;
  :show-clear=&quot;false&quot;
&gt;
&lt;/dt-filter-pill&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <dt-filter-pill
    :model-value="[{name: '0–5 min', active: true}, {name: '5–15 min'}, {name: '15–30 min'}, {name: '30+ min'}]"
    label="Duration"
    :show-clear="false"
  >
  </dt-filter-pill>
</dt-stack>
</code-example>
<h3 id="defer-selection" tabindex="-1" class="d-docsite--header-3">Defer selection <a class="header-anchor d-link d-docsite--link d-link" href="#defer-selection" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Setting <code v-pre>defer-selection</code> holds checkbox changes in a pending state until Apply is clicked.
Cancel, Escape, or clicking outside discards pending changes.</p>
<code-example source-code='&lt;dt-filter-pill
  :model-value=&quot;[{name: &#39;Email&#39;}, {name: &#39;Phone&#39;, active: true}, {name: &#39;Chat&#39;}, {name: &#39;Social&#39;}, {name: &#39;SMS&#39;}]&quot;
  label=&quot;Channel&quot;
  end-tooltip-text=&quot;Remove&quot;
  defer-selection
  popover-footer-class=&quot;d-pie-200 d-py-150&quot;
&gt;
&lt;/dt-filter-pill&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <dt-filter-pill
    :model-value="[{name: 'Email'}, {name: 'Phone', active: true}, {name: 'Chat'}, {name: 'Social'}, {name: 'SMS'}]"
    label="Channel"
    end-tooltip-text="Remove"
    defer-selection
    popover-footer-class="d-pie-200 d-py-150"
  >
  </dt-filter-pill>
</dt-stack>
</code-example>
<h3 id="dropdown" tabindex="-1" class="d-docsite--header-3">Dropdown <a class="header-anchor d-link d-docsite--link d-link" href="#dropdown" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Setting <code v-pre>use-dropdown</code> switches the overlay from a popover to a dropdown with keyboard-navigable
list items. This provides arrow key navigation, highlight management, and Enter/Space selection
out of the box — ideal for single-select filter patterns.</p>
<code-example source-code='&lt;dt-filter-pill
  :model-value=&quot;[{name: &amp;apos;All Conversations&amp;apos;}, {name: &amp;apos;Only Calls&amp;apos;}, {name: &amp;apos;Only Meetings&amp;apos;}, {name: &amp;apos;Only Digital&amp;apos;}]&quot;
  :start-tooltip-text=&quot;selectedType !== &amp;apos;All Conversations&amp;apos;
    ? &amp;apos;Conversation type&amp;apos;
    : &amp;apos;&amp;apos;&quot;
  end-tooltip-text=&quot;Remove&quot;
  use-dropdown
  @clear=&quot;resetType&quot;
&gt;
  &lt;template #default&gt;
    {{ selectedType === &amp;apos;All Conversations&amp;apos;
      ? &amp;apos;Conversation type&amp;apos;
      : selectedType }}
  &lt;/template&gt;
  &lt;template #content=&quot;{ close }&quot;&gt;
    &lt;dt-list-item
      v-for=&quot;filter in conversationTypes&quot;
      :key=&quot;filter.name&quot;
      role=&quot;menuitem&quot;
      navigation-type=&quot;arrow-keys&quot;
      :selected=&quot;filter.name === selectedType&quot;
      @click=&quot;selectType(filter.name, close)&quot;
    &gt;
      {{ filter.name }}
    &lt;/dt-list-item&gt;
  &lt;/template&gt;
&lt;/dt-filter-pill&gt;'>
<dt-stack direction="row" gap="100">
  <dt-filter-pill
    v-model="dropdownTypes"
    :start-tooltip-text="selectedDropdownType !== 'All Conversations'
      ? 'Conversation type'
      : ''"
    end-tooltip-text="Remove"
    use-dropdown
    @clear="resetDropdownType"
  >
    <template #default>
      {{ selectedDropdownType === 'All Conversations'
        ? 'Conversation type'
        : selectedDropdownType }}
    </template>
    <template #content="{ close }">
      <dt-list-item
        v-for="filter in dropdownTypes"
        :key="filter.name"
        role="menuitem"
        navigation-type="arrow-keys"
        :selected="filter.name === selectedDropdownType"
        @click="selectDropdownType(filter.name, close)"
      >
        {{ filter.name }}
      </dt-list-item>
    </template>
  </dt-filter-pill>
</dt-stack>
</code-example>
<h2 id="slots" tabindex="-1" class="d-docsite--header-2">Slots <a class="header-anchor d-link d-docsite--link d-link" href="#slots" aria-hidden="true">#</a></h2>
<h3 id="default" tabindex="-1" class="d-docsite--header-3">Default <a class="header-anchor d-link d-docsite--link d-link" href="#default" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Using the <code v-pre>default</code> slot, you can override the <code v-pre>label</code> prop.</p>
<h4 class="d-docsite--header-4">Example: Count</h4>
<p class="d-docsite--paragraph">Using the <code v-pre>default</code> scoped slot, you can display a count of active filters alongside the label.</p>
<code-example source-code='&lt;dt-filter-pill
  :model-value=&quot;[{name: &#39;Address&#39;, active: true}, {name: &#39;Call Purpose&#39;, active: true}, {name: &#39;Action Item&#39;}, {name: &#39;Negative Sentiment&#39;}, {name: &#39;Warranty Inquiry&#39;, active: true}]&quot;
  label=&quot;Contact centers&quot;
  end-tooltip-text=&quot;Remove&quot;
&gt;
  &lt;template #default=&quot;{ label, filters, activeFilters }&quot;&gt;
    {{ label }}&lt;template v-if=&quot;activeFilters.length&quot;&gt;:
    &lt;strong&gt;
      {{ activeFilters.length === filters.length ? &#39;All&#39; : activeFilters.length }}
    &lt;/strong&gt;&lt;/template&gt;
  &lt;/template&gt;
&lt;/dt-filter-pill&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <dt-filter-pill
    :model-value="[{name: 'Address', active: true}, {name: 'Call Purpose', active: true}, {name: 'Action Item'}, {name: 'Negative Sentiment'}, {name: 'Warranty Inquiry', active: true}]"
    label="Contact centers"
    end-tooltip-text="Remove"
  >
    <template #default="{ label, filters, activeFilters }">
      {{ label }}<template v-if="activeFilters.length">:
      <strong>
        {{ activeFilters.length === filters.length ? 'All' : activeFilters.length }}
      </strong></template>
    </template>
  </dt-filter-pill>
</dt-stack>
</code-example>
<h4 class="d-docsite--header-4">Example: Active filter list</h4>
<p class="d-docsite--paragraph">Shows the first active filter name using <code v-pre>activeFilterList</code>, with overflow count for remaining selections (e.g., &quot;Email +2&quot;).</p>
<code-example source-code='&lt;dt-filter-pill
  :model-value=&quot;[{name: &#39;Email&#39;, active: true}, {name: &#39;Phone&#39;, active: true}, {name: &#39;Chat&#39;, active: true}, {name: &#39;Social&#39;}, {name: &#39;SMS&#39;}]&quot;
  label=&quot;Channel&quot;
  end-tooltip-text=&quot;Remove&quot;
&gt;
  &lt;template #default=&quot;{ label, filters, activeFilters, activeFilterList, activeFilterOverflow }&quot;&gt;
    {{ label }}&lt;template v-if=&quot;activeFilters.length&quot;&gt;:
    &lt;strong&gt;
      {{ activeFilters.length === filters.length ? &#39;All&#39; : activeFilterList }}
    &lt;/strong&gt;
    &lt;template v-if=&quot;activeFilterOverflow&quot;&gt; {{ activeFilterOverflow }}&lt;/template&gt;&lt;/template&gt;
  &lt;/template&gt;
&lt;/dt-filter-pill&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <dt-filter-pill
    :model-value="[{name: 'Email', active: true}, {name: 'Phone', active: true}, {name: 'Chat', active: true}, {name: 'Social'}, {name: 'SMS'}]"
    label="Channel"
    end-tooltip-text="Remove"
  >
    <template #default="{ label, filters, activeFilters, activeFilterList, activeFilterOverflow }">
      {{ label }}<template v-if="activeFilters.length">:
      <strong>
        {{ activeFilters.length === filters.length ? 'All' : activeFilterList }}
      </strong>
      <template v-if="activeFilterOverflow"> {{ activeFilterOverflow }}</template></template>
    </template>
  </dt-filter-pill>
</dt-stack>
</code-example>
<h4 class="d-docsite--header-4">Example: Radio selection</h4>
<p class="d-docsite--paragraph">Combining the <code v-pre>default</code> and <code v-pre>content</code> slots with a radio group creates a single-select filter.
The label updates to show the selected option, and a clear button resets to the default.</p>
<code-example source-code='&lt;dt-filter-pill
  :model-value=&quot;[{name: &#39;All Conversations&#39;}, {name: &#39;Only Calls&#39;}, {name: &#39;Only Meetings&#39;}, {name: &#39;Only Digital&#39;}]&quot;
  :start-tooltip-text=&quot;selectedConversationType !== &#39;All Conversations&#39;
    ? &#39;Conversation type&#39;
    : &#39;&#39;&quot;
  end-tooltip-text=&quot;Remove&quot;
  @clear=&quot;selectedConversationType = &#39;All Conversations&#39;&quot;
&gt;
  &lt;template #default&gt;
    {{ selectedConversationType === &#39;All Conversations&#39;
      ? &#39;Conversation type&#39;
      : selectedConversationType }}
  &lt;/template&gt;
  &lt;template #content&gt;
    &lt;dt-radio-group
      v-model=&quot;selectedConversationType&quot;
      name=&quot;conversation-type-doc-filter&quot;
    &gt;
      &lt;dt-radio
        v-for=&quot;filter in conversationTypes&quot;
        :key=&quot;filter.name&quot;
        :label=&quot;filter.name&quot;
        :value=&quot;filter.name&quot;
        @update:model-value=&quot;$event =&gt; selectedConversationType = $event&quot;
      /&gt;
    &lt;/dt-radio-group&gt;
  &lt;/template&gt;
&lt;/dt-filter-pill&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <dt-filter-pill
    :model-value="[{name: 'All Conversations'}, {name: 'Only Calls'}, {name: 'Only Meetings'}, {name: 'Only Digital'}]"
    :start-tooltip-text="selectedConversationType !== 'All Conversations'
      ? 'Conversation type'
      : ''"
    end-tooltip-text="Remove"
    @clear="selectedConversationType = 'All Conversations'"
  >
    <template #default>
      {{ selectedConversationType === 'All Conversations'
        ? 'Conversation type'
        : selectedConversationType }}
    </template>
    <template #content>
      <dt-radio-group
        v-model="selectedConversationType"
        name="conversation-type-doc-filter"
      >
        <dt-radio
          v-for="filter in conversationTypes"
          :key="filter.name"
          :label="filter.name"
          :value="filter.name"
          @update:model-value="$event => selectedConversationType = $event"
        />
      </dt-radio-group>
    </template>
  </dt-filter-pill>
</dt-stack>
</code-example>
<h3 id="content" tabindex="-1" class="d-docsite--header-3">Content <a class="header-anchor d-link d-docsite--link d-link" href="#content" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Using the <code v-pre>content</code> slot, you can override the popover content with custom markup.</p>
<code-example source-code='&lt;dt-filter-pill
  :model-value=&quot;[{name: &#39;Contains&#39;}, {name: &#39;Starts with&#39;}]&quot;
  label=&quot;Keyword&quot;
&gt;
  &lt;template #content&gt;
    Enter a keyword to filter results
  &lt;/template&gt;
&lt;/dt-filter-pill&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100">
  <dt-filter-pill
    :model-value="[{name: 'Contains'}, {name: 'Starts with'}]"
    label="Keyword"
  >
    <template #content>
      Enter a keyword to filter results
    </template>
  </dt-filter-pill>
</dt-stack>
</code-example>
<h2 id="content-mode" tabindex="-1" class="d-docsite--header-2">Content Mode <a class="header-anchor d-link d-docsite--link d-link" href="#content-mode" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Filter Pill popover content renders outside the DOM tree. Use the <code v-pre>contentMode</code> prop to apply color mode (invert, light, dark) to the positioned content. See <a href="/components/mode-island.html#positioned-components" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Positioned Components</a> for details.</p>
<code-example source-code='&lt;dt-filter-pill content-mode=&quot;invert&quot;&gt;...&lt;/dt-filter-pill&gt;
&lt;dt-filter-pill content-mode=&quot;dark&quot;&gt;...&lt;/dt-filter-pill&gt;
&lt;dt-filter-pill content-mode=&quot;light&quot;&gt;...&lt;/dt-filter-pill&gt;'>
<dt-filter-pill content-mode="invert" label="Inverted" :model-value="[{ name: 'Orange', active: true }, { name: 'Apple' }]" end-tooltip-text="Remove" />
</code-example>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="filterPill"></component-vue-api><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The filter pill is built on <code v-pre>DtButton</code> and <code v-pre>DtPopover</code>/<code v-pre>DtDropdown</code>, inheriting their keyboard and screen reader support.</p>
<h3 id="keyboard-interaction" tabindex="-1" class="d-docsite--header-3">Keyboard interaction <a class="header-anchor d-link d-docsite--link d-link" href="#keyboard-interaction" aria-hidden="true">#</a></h3>
<table>
<thead>
<tr>
<th>Key</th>
<th>Action</th>
</tr>
</thead>
<tbody>
<tr>
<td><kbd>Enter</kbd> / <kbd>Space</kbd></td>
<td>Opens the popover or dropdown; toggles checkboxes inside</td>
</tr>
<tr>
<td><kbd>Arrow Up</kbd> / <kbd>Arrow Down</kbd></td>
<td>Opens the popover; navigates list items in dropdown mode</td>
</tr>
<tr>
<td><kbd>Escape</kbd></td>
<td>Closes the popover or dropdown, discarding pending changes in deferred mode</td>
</tr>
<tr>
<td><kbd>Tab</kbd></td>
<td>Moves focus between the primary button, checkboxes, footer actions, and the clear button</td>
</tr>
</tbody>
</table>
<h3 id="aria-attributes" tabindex="-1" class="d-docsite--header-3">ARIA attributes <a class="header-anchor d-link d-docsite--link d-link" href="#aria-attributes" aria-hidden="true">#</a></h3>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">The primary button uses <code v-pre>aria-disabled=&quot;true&quot;</code> in read-only mode instead of <code v-pre>disabled</code>, preserving focusability while indicating the control is not interactive.</li>
<li class="d-docsite--list-element">The checkbox group inside the popover receives <code v-pre>aria-label</code> set to the pill's <code v-pre>label</code> prop, giving screen readers context for the list of options.</li>
<li class="d-docsite--list-element">The clear button uses <code v-pre>aria-label</code> set to <code v-pre>endTooltipText</code> (or a localized default), ensuring the icon-only button has an accessible name.</li>
</ul>
<h3 id="labelling-guidelines" tabindex="-1" class="d-docsite--header-3">Labelling guidelines <a class="header-anchor d-link d-docsite--link d-link" href="#labelling-guidelines" aria-hidden="true">#</a></h3>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Always provide a <code v-pre>label</code> prop or populate the <code v-pre>default</code> slot so the pill has visible text.</li>
<li class="d-docsite--list-element">When active filters change the visible label (e.g., radio selection patterns), set <code v-pre>start-tooltip-text</code> to the original label so the full context remains available on hover and to assistive technology.</li>
<li class="d-docsite--list-element">Provide <code v-pre>end-tooltip-text</code> for the clear button to give it a meaningful accessible name (e.g., &quot;Remove filter&quot;).</li>
</ul>
<h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="filter-pill"></component-class-table></div></template>


<script setup>
import { ref, computed } from 'vue';

const heroConversationTypes = ref([
  {name: 'All Conversations'},
  {name: 'Only Calls'},
  {name: 'Only Meetings'},
  {name: 'Only Digital'},
]);
const selectedHeroConversationType = computed(() => {
  return heroConversationTypes.value.find(f => f.active)?.name || 'All Conversations';
});
function selectHeroConversationType (name, close) {
  heroConversationTypes.value.forEach(f => {
    f.active = name !== 'All Conversations' && f.name === name;
  });
  close();
}
function resetHeroConversationType () {
  heroConversationTypes.value.forEach(f => { f.active = false; });
}
const heroHasActiveFilters = computed(() => {
  return momentCount.value.some(f => f.active) ||
    selectedHeroConversationType.value !== 'All Conversations' ||
    deferredFilters.value.some(f => f.active);
});
function resetHeroFilters () {
  momentCount.value.forEach(f => { delete f.active; });
  heroConversationTypes.value.forEach(f => { f.active = false; });
  deferredFilters.value.forEach(f => { delete f.active; });
}
const deferredFilters = ref([
  {name: 'Email'},
  {name: 'Phone', active: true },
  {name: 'Chat'},
  {name: 'Social'},
  {name: 'SMS'},
]);
const momentCount = ref([
  {name: 'Address', active: true},
  {name: 'Call Purpose', active: true},
  {name: 'Action Item'},
  {name: 'Negative Sentiment'},
  {name: 'Warranty Inquiry', active: true },
]);
const conversationTypes = ref([
  {name: 'All Conversations'},
  {name: 'Only Calls'},
  {name: 'Only Meetings'},
  {name: 'Only Digital'},
]);
const dropdownTypes = ref([
  {name: 'All Conversations'},
  {name: 'Only Calls'},
  {name: 'Only Meetings'},
  {name: 'Only Digital'},
]);
const selectedConversationType = computed({
  get () {
    return conversationTypes.value.find(f => f.active)?.name || 'All Conversations';
  },
  set (newValue) {
    conversationTypes.value.forEach(f => {
      f.active = f.name === newValue && newValue !== 'All Conversations';
    });
  },
});
const selectedDropdownType = computed(() => {
  return dropdownTypes.value.find(f => f.active)?.name || 'All Conversations';
});
function selectDropdownType (name, close) {
  dropdownTypes.value.forEach(f => {
    f.active = name !== 'All Conversations' && f.name === name;
  });
  close();
}
function resetDropdownType () {
  dropdownTypes.value.forEach(f => { f.active = false; });
}
const sizes = Object.keys(window.DIALTONE_CONSTANTS.BUTTON_SIZE_MODIFIERS).filter(k => /^\d+$/.test(k));
</script>